/**
 * Le fasce di Film e Serie TV: **quali sono, cosa contengono, in che ordine**.
 *
 * ## Perché sta nel dominio
 *
 * Perché è tutto conto e nessun disegno. Le fasce del prototipo v3 (`vodVM`) sono cinque decisioni —
 * quali generi sono «i tuoi», quali titoli sono consigliati, quali si stanno guardando, quali sono
 * arrivati per ultimi, e quando una fascia non va mostrata affatto — e ognuna si prova in Node, senza
 * un browser e senza un catalogo vero. Quel che resta fuori è disegnare una locandina.
 *
 * ## Il genere è la categoria
 *
 * Sul prototipo i generi erano una colonna di dati finti. Su un catalogo vero **sono le categorie**:
 * un fornitore VOD le chiama «Azione», «Commedia», «Thriller», sono un insieme chiuso, e ogni titolo
 * ne porta una. Il campo `genre` della scheda è invece testo libero, spesso vuoto e spesso multiplo
 * («Azione, Thriller»): raggruppare per quello darebbe una fascia per ogni combinazione di parole, e
 * l'utente vedrebbe «Azione», «Azione, Thriller» e «Thriller, Azione» come tre generi diversi.
 *
 * ## Una fascia vuota non si disegna
 *
 * `«Consigliati per te»` senza consigli è peggio di niente: insegna che quella riga è finta. Chi non
 * ha mai guardato niente non ha consigli né titoli da riprendere, ed è il caso **normale** al primo
 * avvio — quindi le fasce vuote spariscono, e la vetrina di chi apre l'app per la prima volta comincia
 * dagli ultimi arrivi.
 */

import { compareCatalogNames } from './catalogSort.js';
import {
  continueWatching,
  latestArrivals,
  recommendations,
  watchStateOf,
  type VodItem,
  type VodKind,
  type WatchProgress,
  type WatchState,
} from './catalog.js';

/** Quanti titoli entrano in una fascia. Oltre, c'è «Vedi tutti» con il conto vero. */
export const SHELF_ROW_LIMIT = 20;

/**
 * Su cosa si raggruppa: le categorie, oppure l'iniziale del titolo.
 *
 * ## Perché esiste la seconda
 *
 * Perché su un'installazione vera le categorie **possono non esserci**. Sul catalogo di sviluppo, il
 * 28-09-2026: 25 film su 11535 ne hanno una, e le serie nessuna — e il campo `genre` è vuoto su tutti.
 * Con il solo raggruppamento per categoria la sezione Film mostrava una fascia sola, «Ultimi arrivi»,
 * con dieci titoli su undicimila: un catalogo pieno dietro una vetrina vuota.
 *
 * L'iniziale **non inventa niente**: è il titolo, che c'è sempre. Non è una categoria travestita e
 * non pretende di esserlo — si chiama «A», «B», «C», e chi guarda capisce in un'occhiata cos'è.
 * L'alternativa era lasciare la sezione inutilizzabile finché qualcuno non reimporta il catalogo, che
 * è una condizione che non dipende da chi guarda la televisione.
 */
export type VodGrouping = 'category' | 'initial';

/**
 * Quanta parte del catalogo deve avere una categoria **con un nome** perché valga la pena usarle.
 *
 * Metà. Sotto, le fasce per categoria coprirebbero una minoranza e tutto il resto finirebbe fuori da
 * ogni fascia — che è peggio di non averle: un catalogo che sembra piccolo. Una soglia e non «almeno
 * una», perché «almeno una» è esattamente il caso osservato, con l'unica categoria che copriva lo
 * 0,2% dei titoli.
 */
export const CATEGORY_COVERAGE_THRESHOLD = 0.5;

/** Come conviene raggruppare **questo** catalogo. Si decide una volta, e vale per tutta la vetrina. */
export function chooseGrouping(
  items: ReadonlyArray<VodItem>,
  categoryNames: ReadonlyMap<string, string>,
): VodGrouping {
  if (items.length === 0) return 'category';
  let named = 0;
  for (const item of items) {
    const name = categoryNames.get(item.categoryId);
    if (name !== undefined && name !== '') named += 1;
  }
  return named / items.length >= CATEGORY_COVERAGE_THRESHOLD ? 'category' : 'initial';
}

/**
 * L'iniziale sotto cui va un titolo.
 *
 * Tutto ciò che non è una lettera finisce sotto `#`: i titoli che cominciano per cifra, per virgoletta
 * o per emoji esistono e sono parecchi su un catalogo importato. Senza un raggruppamento loro
 * sparirebbero dalla vetrina, che è il modo peggiore di gestirli — invisibili invece che in fondo.
 */
export function initialOf(title: string): string {
  const first = title.trim().charAt(0).toUpperCase();
  return first >= 'A' && first <= 'Z' ? first : '#';
}

/** Il gruppo di un titolo — identificativo e nome da mostrare — o `null` se non ne ha uno. */
export function groupOf(
  item: VodItem,
  grouping: VodGrouping,
  categoryNames: ReadonlyMap<string, string>,
): { readonly id: string; readonly name: string } | null {
  if (grouping === 'initial') {
    const letter = initialOf(item.title);
    return { id: letter, name: letter };
  }
  if (item.categoryId === '') return null;
  const name = categoryNames.get(item.categoryId);
  // Una categoria che il listino non nomina **non diventa una fascia**: sarebbe una fascia intitolata
  // con un UUID, lo stesso difetto già visto nella colonna della guida.
  if (name === undefined || name === '') return null;
  return { id: item.categoryId, name };
}

/** Quanti generi si mostrano come «i tuoi», e quante fasce di genere si aprono sotto. */
export const YOUR_GENRES_LIMIT = 5;
export const GENRE_ROWS_LIMIT = 3;

/**
 * Perché una fascia esiste.
 *
 * Un codice e non un titolo: il titolo è testo di prodotto e lo compone l'i18n in sette lingue
 * (14-LINGUE-E-TESTI §1). Le fasce di genere portano invece il nome della categoria, che **non si
 * traduce** — arriva dal catalogo dell'operatore.
 */
export type VodShelfRowKind = 'recommended' | 'continue' | 'latest' | 'genre';

export interface VodShelfItem {
  readonly item: VodItem;
  readonly state: WatchState;
  /**
   * Il titolo che ha generato il consiglio, o `null`.
   *
   * Si mostra **sempre** dove c'è: «Consigliati per te» senza il perché è indistinguibile da una
   * fascia riempita a caso, e dopo tre settimane si smette di guardarla (05-CONTRATTO-API §4.4).
   */
  readonly because: string | null;
}

export interface VodShelfRow {
  readonly kind: VodShelfRowKind;
  /** La categoria, per le fasce di genere; `null` per le altre tre. */
  readonly genreId: string | null;
  /** Il nome della categoria, non tradotto. Vuoto per le altre tre. */
  readonly genreName: string;
  readonly items: ReadonlyArray<VodShelfItem>;
  /** Quanti titoli ha in tutto la categoria: è il numero di «Vedi tutti», e può superare `items`. */
  readonly total: number;
}

export interface VodGenre {
  readonly id: string;
  readonly name: string;
  readonly count: number;
  /**
   * Quante sottocategorie ha, quando ne ha.
   *
   * Si mostra in ambra sotto il nome — «3 sottocategorie» — e serve a una cosa sola: far sapere
   * **prima di premere** che lì dentro non c'è una griglia piatta ma un altro livello. Senza, chi
   * apre «Azione» e trova delle pastiglie non capisce da dove sono uscite.
   *
   * Assente quando la sorgente non è un albero: un catalogo raggruppato per iniziale non ha
   * sottocategorie, e scriverci «0 sottocategorie» sarebbe rumore.
   */
  readonly subcategoryCount?: number;
}

export interface VodShelf {
  /** Su cosa sono raggruppate le fasce: chi disegna lo dice a chi guarda, invece di lasciarlo
   *  indovinare da nomi di fascia che sono lettere singole. */
  readonly grouping: VodGrouping;
  readonly rows: ReadonlyArray<VodShelfRow>;
  /** Le pastiglie in alto: i generi di chi guarda, poi le prime del catalogo nell'ordine del pannello. */
  readonly chips: ReadonlyArray<VodGenre>;
  /** Tutti i generi, per la schermata che li elenca. Nell'ordine in cui arrivano dall'API, che è
   *  `categories.position`; è quella schermata a riordinarli per nome, perché lì la domanda è
   *  «dov'è quello che cerco» e non «da dove comincio». */
  readonly genres: ReadonlyArray<VodGenre>;
}

/**
 * I generi, con quanti titoli ciascuno.
 *
 * Il nome arriva dalle categorie; una categoria che il listino non nomina resta con
 * l'identificativo **fuori** dall'elenco invece di comparire come UUID: era il difetto che si era
 * già visto nella colonna della guida, e ripeterlo qui significherebbe una fascia intitolata
 * `9f9ddebc-2c62-…`.
 */
export function vodGenres(
  items: ReadonlyArray<VodItem>,
  categoryNames: ReadonlyMap<string, string>,
  grouping: VodGrouping = 'category',
): ReadonlyArray<VodGenre> {
  const counts = new Map<string, { name: string; count: number }>();
  for (const item of items) {
    const group = groupOf(item, grouping, categoryNames);
    if (group === null) continue;
    const before = counts.get(group.id);
    counts.set(group.id, { name: group.name, count: (before?.count ?? 0) + 1 });
  }
  const out: VodGenre[] = [];
  for (const [id, entry] of counts) out.push({ id, name: entry.name, count: entry.count });

  /*
    Le iniziali si ordinano **alfabeticamente**.

    Un alfabeto ordinato per quanti titoli ha ciascuna lettera non è un alfabeto: chi cerca «Matrix»
    scorre fino alla M, e la M dev'essere fra la L e la N. È la differenza fra un elenco e un
    ordinamento che sembra casuale a chi lo usa.
  */
  if (grouping === 'initial') {
    return out.sort((a, b) => compareCatalogNames(a.name, b.name));
  }

  /*
    Le categorie tengono **l'ordine con cui sono arrivate**, che è `categories.position`.

    Prima ordinavo per ricchezza, e su un catalogo vero la prima pastiglia — quella su cui si apre il
    fuoco — diventava la categoria con più titoli: «Cinema Erotico – XXX VOD». Nessuno l'aveva messa
    lì, l'aveva scelta un `count`. È lo stesso difetto già corretto nella colonna della guida
    (`guideCategories.ts`): l'ordine è dichiarato a monte e non va ricalcolato a valle. `categoryNames`
    è una `Map`, e una `Map` conserva l'ordine d'inserimento: è quello della risposta dell'API.

    Quelle che l'elenco delle categorie non nomina non ci sono proprio — `groupOf` le scarta — quindi
    qui non serve un ramo per il caso «senza posizione».
  */
  const order = new Map<string, number>();
  let at = 0;
  for (const id of categoryNames.keys()) {
    order.set(id, at);
    at += 1;
  }
  return out.sort((a, b) => {
    const left = order.get(a.id);
    const right = order.get(b.id);
    if (left !== undefined && right !== undefined) return left - right;
    if (left !== undefined) return -1;
    if (right !== undefined) return 1;
    return compareCatalogNames(a.name, b.name);
  });
}

/**
 * `yourGenres` del prototipo: i generi pesati su ciò che si è guardato.
 *
 * **Dove il prototipo riempiva, qui si cambia criterio.** Lì, quando la storia non bastava a fare
 * cinque generi, l'elenco veniva completato con cinque nomi scritti a mano («Azione», «Commedia»,
 * …) — dati finti, che su un catalogo vero sarebbero pastiglie che portano a una schermata vuota o
 * che non esistono affatto. Qui si completa con le **prime categorie del catalogo**, nell'ordine in
 * cui l'operatore le ha messe: è una risposta diversa alla stessa domanda («da dove comincio»), ed è
 * vera.
 *
 * Non si completa con le **più ricche**, che era il criterio di prima: il numero di titoli non è una
 * raccomandazione, e su un catalogo vero portava in cima — dove si apre il fuoco — la categoria per
 * adulti. Il primo posto è una scelta editoriale, e chi la fa è chi amministra il catalogo.
 */
export function yourVodGenres(
  items: ReadonlyArray<VodItem>,
  progress: WatchProgress,
  categoryNames: ReadonlyMap<string, string>,
  limit: number = YOUR_GENRES_LIMIT,
  grouping: VodGrouping = 'category',
): ReadonlyArray<VodGenre> {
  const all = vodGenres(items, categoryNames, grouping);
  const byId = new Map(all.map((genre) => [genre.id, genre]));

  /*
    Con le iniziali **non si pesa niente**: si mostra l'alfabeto.

    «I tuoi generi: M, P, S» perché si sono guardati tre film che cominciano così non dice niente a
    nessuno — è un accostamento casuale presentato come un gusto. Le iniziali sono un indice, e un
    indice si mostra in ordine.
  */
  if (grouping === 'initial') return all.slice(0, limit);

  const weights = new Map<string, number>();
  for (const item of items) {
    const state = watchStateOf(item, progress);
    const weight = state.watched ? 2 : state.started ? 1 : 0;
    if (weight === 0) continue;
    weights.set(item.categoryId, (weights.get(item.categoryId) ?? 0) + weight);
  }

  const watched = [...weights.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([id]) => byId.get(id))
    .filter((genre): genre is VodGenre => genre !== undefined);

  if (watched.length >= limit) return watched.slice(0, limit);

  // Si completa con i più ricchi, **senza ripetere** quelli già entrati per merito della storia.
  const chosen = new Set(watched.map((genre) => genre.id));
  const filler = all.filter((genre) => !chosen.has(genre.id));
  return [...watched, ...filler].slice(0, limit);
}

export interface VodShelfInput {
  readonly items: ReadonlyArray<VodItem>;
  readonly kind: VodKind;
  readonly progress: WatchProgress;
  /** Gli ultimi titoli toccati, dal più recente: decidono l'ordine di «Continua a guardare». */
  readonly recentIds: ReadonlyArray<string>;
  readonly categoryNames: ReadonlyMap<string, string>;
  /** Forzato solo dalle prove: fuori di lì lo decide `chooseGrouping` guardando il catalogo. */
  readonly grouping?: VodGrouping;
}

/** `vodVM` del prototipo: le tre fasce fisse più le prime tre di genere, saltando le vuote. */
export function buildVodShelf(input: VodShelfInput): VodShelf {
  const { items, kind, progress, recentIds, categoryNames } = input;
  const mine = items.filter((item) => item.kind === kind);
  // Si decide **una volta** su cosa raggruppare, e vale per pastiglie, fasce ed elenco dei generi:
  // tre risposte diverse alla stessa domanda darebbero una vetrina che si contraddice da sola.
  const grouping = input.grouping ?? chooseGrouping(mine, categoryNames);
  const genres = vodGenres(mine, categoryNames, grouping);
  const chips = yourVodGenres(mine, progress, categoryNames, YOUR_GENRES_LIMIT, grouping);

  const decorate = (item: VodItem, because: string | null = null): VodShelfItem => ({
    item,
    state: watchStateOf(item, progress),
    because,
  });

  const fixed: VodShelfRow[] = [
    {
      kind: 'recommended',
      genreId: null,
      genreName: '',
      items: recommendations(mine, progress, kind)
        .slice(0, SHELF_ROW_LIMIT)
        .map((recommendation) => decorate(recommendation.item, recommendation.becauseOfTitle)),
      total: 0,
    },
    {
      kind: 'continue',
      genreId: null,
      genreName: '',
      items: continueWatching(mine, progress, recentIds, kind)
        .slice(0, SHELF_ROW_LIMIT)
        .map((item) => decorate(item)),
      total: 0,
    },
    {
      kind: 'latest',
      genreId: null,
      genreName: '',
      items: latestArrivals(mine, kind).map((item) => decorate(item)),
      total: 0,
    },
  ];

  const genreRows: VodShelfRow[] = chips.slice(0, GENRE_ROWS_LIMIT).map((genre) => {
    const inGenre = mine.filter((item) => groupOf(item, grouping, categoryNames)?.id === genre.id);
    return {
      kind: 'genre' as const,
      genreId: genre.id,
      genreName: genre.name,
      items: inGenre.slice(0, SHELF_ROW_LIMIT).map((item) => decorate(item)),
      total: inGenre.length,
    };
  });

  return {
    rows: [...fixed, ...genreRows].filter((row) => row.items.length > 0),
    chips,
    genres,
    grouping,
  };
}

/**
 * La vetrina ridotta a **un genere solo**, per intero.
 *
 * È dove portano le pastiglie in alto e il tasto «Vedi tutti». Una fascia sola, con tutti i titoli e
 * non i primi venti: il senso di «Vedi tutti» è appunto che non ne manchi nessuno, e un «Vedi tutti»
 * che ne mostra venti su duecento è una promessa non mantenuta.
 *
 * Niente pastiglie: si sta già dentro un genere, e ripresentarle lì darebbe un cursore che gira su sé
 * stesso. Si esce col tasto Indietro.
 */
export function buildGenreShelf(input: {
  readonly items: ReadonlyArray<VodItem>;
  readonly kind: VodKind;
  readonly progress: WatchProgress;
  readonly genreId: string;
  readonly genreName: string;
  readonly categoryNames: ReadonlyMap<string, string>;
  readonly grouping?: VodGrouping;
}): VodShelf {
  const { items, kind, progress, genreId, genreName, categoryNames } = input;
  const ofKind = items.filter((item) => item.kind === kind);
  /*
    Il raggruppamento si **ricalcola dallo stesso insieme** da cui l'aveva calcolato la vetrina.

    Passare `genreId` senza dire su cosa era stato costruito sarebbe un identificativo senza il suo
    sistema di riferimento: «A» è una lettera per un catalogo senza categorie e non significa niente
    per uno che ne ha — la stessa stringa che apre due insiemi diversi a seconda di chi la legge.
  */
  const grouping = input.grouping ?? chooseGrouping(ofKind, categoryNames);
  const mine = ofKind.filter((item) => groupOf(item, grouping, categoryNames)?.id === genreId);
  const row: VodShelfRow = {
    kind: 'genre',
    genreId,
    genreName,
    items: mine.map((item) => ({ item, state: watchStateOf(item, progress), because: null })),
    // Uguale al numero di titoli mostrati: non c'è un «Vedi tutti» da offrire, si è già dentro.
    total: mine.length,
  };
  return {
    rows: row.items.length > 0 ? [row] : [],
    chips: [],
    genres: vodGenres(ofKind, categoryNames, grouping),
    grouping,
  };
}

/**
 * I titoli simili, per la fascia «Potrebbe piacerti anche» in fondo alla scheda.
 *
 * Stesso genere, sé stesso escluso. Non è un motore di raccomandazione: è la domanda «cos'altro c'è
 * di questo tipo», ed è quella che si fa guardando una scheda.
 */
export const SIMILAR_LIMIT = 12;

export function similarTo(
  item: VodItem,
  catalog: ReadonlyArray<VodItem>,
  progress: WatchProgress,
): ReadonlyArray<VodShelfItem> {
  return catalog
    .filter(
      (other) =>
        other.id !== item.id && other.kind === item.kind && other.categoryId === item.categoryId,
    )
    .slice(0, SIMILAR_LIMIT)
    .map((other) => ({ item: other, state: watchStateOf(other, progress), because: null }));
}
